import {
  Box,
  Typography,
  useMediaQuery,
  CircularProgress,
  Button,
} from "@mui/material";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { ArrowRight, X } from 'lucide-react';
import { useHotel } from "../context/HotelContext";
import { useState, useEffect } from "react";
import LoadingGif from "../components/LoadingGif";


const HotelOnboarding = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:850px)");
  const { initializeDraft, listUserHotels, loading } = useHotel();
  const [drafting, setDrafting] = useState(false);
  const [existingHotel, setExistingHotel] = useState({
    hotelId: "",
    hotelName: "",
    status: "",
    currentStep: "",
  });

  useEffect(() => {
    const checkExistingHotels = async () => {
      try {
        const hotels = await listUserHotels();
        if (hotels.length > 0) {
          setExistingHotel(hotels[0]);
        }
      } catch (error) {
        console.error("Failed to fetch user hotels:", error);
      }
    };

    checkExistingHotels();
  }, [listUserHotels]);

  const handleCreateHotel = async () => {
    try {
      setDrafting(true);
      const hotelId = await initializeDraft();
      navigate(`/hotel/add/info1/${hotelId}`);
    } catch (error) {
      console.error("Failed to create hotel draft:", error);
    } finally {
      setDrafting(false);
    }
  };

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: isMobile ? "100px 20px" : "120px 40px",
  };

  const cardStyle = {
    backgroundColor: "#FFFFFF",
    border: "2px solid #D1D1D1",
    borderRadius: "10px",
    padding: isMobile ? "20px" : "30px",
    width: isMobile ? "100%" : "260px",
    height: isMobile ? "auto" : "160px",
    display: "flex",
    flexDirection: isMobile ? "row" : "column",
    justifyContent: "space-between",
    cursor: "pointer",
    transition: "all 0.3s ease",
    position: "relative",
  };

  const cardHoverStyle = {
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 8px 20px rgba(0, 0, 0, 0.15)",
    }
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {loading ? (
          <LoadingGif />
        ) : (
          <>
            {existingHotel.hotelId && existingHotel.status !== "draft" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  backgroundColor: existingHotel.status === "submitted" ? "#FFF8E1" : existingHotel.status === "approved" ? "#E8F5E9" : "#FFEBEE",
                  border: existingHotel.status === "submitted" ? "1px solid #FFE082" : existingHotel.status === "approved" ? "1px solid #00A63E" : "1px solid #E5194A",
                  borderRadius: "10px",
                  padding: isMobile ? "12px 16px" : "14px 20px",
                  mb: 2,
                  width: "100%",
                  boxSizing: "border-box",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <Box component="img" src={existingHotel.status === "submitted" ? "/icons/hotelSubmitted.svg" : existingHotel.status === "approved" ? "/icons/hotelApproved.svg" : "/icons/hotelRejected.svg"} alt="" sx={{ width: 25, height: "auto", alignSelf: "flex-start" }} />
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontWeight: "700", fontSize: isMobile ? "14px" : "16px", color: existingHotel.status === "submitted" ? "#FFB60D" : existingHotel.status === "approved" ? "#00A63E" : "#E5194A" }}>
                        {existingHotel.status === "submitted" ? t("LicenseUnderReviewTitle") : existingHotel.status === "approved" ? t("LicenseApprovedTitle") : t("LicenseRejectedTitle")}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: existingHotel.status === "submitted" ? "#FFB60D" : existingHotel.status === "approved" ? "#00A63E" : "#E5194A" }}>
                      {existingHotel.status === "submitted" ? t("LicenseUnderReviewDesc") : existingHotel.status === "approved" ? t("LicenseApprovedDesc") : t("LicenseRejectedDesc")}
                    </Typography>
                  </Box>
                </Box>
                <Button sx={{ minWidth: "auto", padding: "6px", color: "#666" }}><X size={15} /></Button>
              </Box>
            )}
            {/* Paw Icon */}
            <img
              src="/icons/hotelPaw.png"
              alt="Hotel Paw"
              style={{
                width: isMobile ? "60px" : "80px",
                height: "auto",
                marginBottom: "12px"
              }}
            />

            {/* Main Heading */}
            <Typography
              variant="h3"
              sx={{
                fontWeight: "bold",
                fontSize: isMobile ? "24px" : "32px",
                mb: 3,
                color: "#000",
              }}
            >
              {t('HotelOnboardingWelcome')}
            </Typography>

            {/* Description Text */}
            <Typography
              sx={{
                fontSize: isMobile ? "14px" : "16px",
                color: "#666",
                maxWidth: "600px",
                mb: 1,
                lineHeight: 1.6,
              }}
            >
              {t('HotelOnboardingDesc1')}
            </Typography>

            <Typography
              sx={{
                fontSize: "16px",
                color: "#666",
                maxWidth: "700px",
                mb: 6,
                lineHeight: 1.6,
              }}
            >
              {t('HotelOnboardingDesc2')}
            </Typography>

            {/* Cards Container */}
            <Box
              sx={{
                display: "flex",
                gap: isMobile ? 3 : 4,
                flexDirection: isMobile ? "column" : "row",
                width: "100%",
                maxWidth: "800px",
                justifyContent: "flex-start",
              }}
            >
              {/* Create Hotel Card */}
              <Box
                disabled={existingHotel.status === "submitted" || existingHotel.status === "approved"}
                onClick={existingHotel.hotelId ? () => navigate("/hotel/" + existingHotel.hotelId + "/information") : handleCreateHotel}
                sx={{
                  ...cardStyle,
                  ...cardHoverStyle,
                }}
              >
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "600",
                        fontSize: "18px",
                        color: "#000",
                      }}
                    >
                      {existingHotel.hotelId ? t('HotelInformation') : t('CreateHotel')}
                    </Typography>
                    <img
                      src={existingHotel.hotelId ? "/icons/HotelInfo.svg" : "/icons/hotelAdd.svg"}
                      alt="Add Hotel"
                      style={{ width: "24px", height: "24px" }}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: isMobile ? "32px" : "48px",
                    height: isMobile ? "32px" : "48px",
                    borderRadius: "50%",
                    backgroundColor: drafting ? "#fff" : "#31BACD",
                    border: drafting ? "2px solid #31BACD" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.1)",
                    }
                  }}
                >
                  {drafting ? (
                    <CircularProgress size={20} color="inherit" sx={{ color: "#31BACD" }} />
                  ) : (
                    <ArrowRight color="#fff" size={24} />
                  )}
                </Box>
              </Box>

              {/* Manage Room Type Card */}
              <Box
                onClick={() => existingHotel?.hotelId && navigate("/hotel/room-management/" + existingHotel.hotelId)}
                sx={{
                  ...cardStyle,
                  ...cardHoverStyle,
                  opacity: !existingHotel?.hotelId ? 0.5 : 1,
                  pointerEvents: !existingHotel?.hotelId ? "none" : "auto",
                  cursor: !existingHotel?.hotelId ? "not-allowed" : "pointer",
                }}
              >
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "600",
                        fontSize: isMobile ? "16px" : "18px",
                        color: "#000",
                      }}
                    >
                      {t('ManageRoomType')}
                    </Typography>
                    <img
                      src="/icons/hotelManage.svg"
                      alt="Manage Hotel"
                      style={{ width: "24px", height: "24px" }}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: isMobile ? "32px" : "48px",
                    height: isMobile ? "32px" : "48px",
                    borderRadius: "50%",
                    backgroundColor: "#31BACD",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.1)",
                    }
                  }}
                >
                  <ArrowRight color="#fff" size={24} />
                </Box>
              </Box>
            </Box>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default HotelOnboarding;
